import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsService } from '../../services/analytics.service';
import { ROUTES } from '../../global';

const useRouteTracking = () => {
  const location = useLocation();
  const previousPath = useRef('');
  const pageStartTime = useRef(Date.now());

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    if (previousPath.current && previousPath.current !== currentPath) {
      const duration = Date.now() - pageStartTime.current;
      const { pageName, section } = getPageInfoFromPath(previousPath.current);
      AnalyticsService.trackPageDuration(pageName, section, duration);
    }

    if (currentPath !== previousPath.current) {
      const { pageName, section } = getPageInfoFromPath(currentPath);
      AnalyticsService.trackPageView(pageName, section);

      if (previousPath.current) {
        AnalyticsService.trackSPANavigation(previousPath.current, currentPath);
      }
    }
    previousPath.current = currentPath;
    pageStartTime.current = Date.now();
  }, [location.pathname, location.search]);

  return null;
};

const getPageInfoFromPath = (path: string) => {
  if (path.includes(ROUTES.monitoring.path)) {
    return { pageName: 'Monitoring', section: 'Monitoring' };
  }
  if (path.includes(ROUTES.personnel.path)) {
    if (path.includes(ROUTES.personnel.vehicles.path)) {
      return { pageName: 'Vehicles', section: 'Personnel' };
    }
    if (path.includes(ROUTES.personnel.teams.path)) {
      return { pageName: 'Teams', section: 'Personnel' };
    }
    if (path.includes(ROUTES.personnel.devices.path)) {
      return { pageName: 'Devices', section: 'Personnel' };
    }
  } else if (path.includes(ROUTES.geofence.path)) {
    return { pageName: 'Geofence', section: 'Geofence' };
  } else if (path.includes(ROUTES.reporting.path)) {
    if (path.includes(ROUTES.reporting.conditionalEvents.path)) {
      return { pageName: 'Conditional Events', section: 'Reporting' };
    }
    if (path.includes(ROUTES.reporting.dailyWorkdayDuration.path)) {
      return { pageName: 'Daily Workday Duration', section: 'Reporting' };
    }
    if (path.includes(ROUTES.reporting.vehicleGeofenceReport.path)) {
      return { pageName: 'Vehicle Geofence Report', section: 'Reporting' };
    }
    if (path.includes(ROUTES.reporting.visitedLocations.path)) {
      return { pageName: 'Visited Locations', section: 'Reporting' };
    }
    if (path.includes(ROUTES.reporting.trips.path)) {
      return { pageName: 'Trips', section: 'Reporting' };
    }
  } else if (path.includes(ROUTES.drivers.path)) {
    return { pageName: 'Drivers', section: 'Drivers' };
  } else if (path.includes(ROUTES.places.path)) {
    if (path.includes(ROUTES.places.locations.path)) {
      return { pageName: 'Locations', section: 'Places' };
    }
    if (path.includes(ROUTES.places.groups.path)) {
      return { pageName: 'Groups', section: 'Places' };
    }
  } else if (path.includes(ROUTES.protocols.path)) {
    if (path.includes(ROUTES.protocols.rules.path)) {
      return { pageName: 'Rules', section: 'Protocols' };
    }
    return { pageName: 'Alerts', section: 'Protocols' };
  } else if (path.includes(ROUTES.admin.path)) {
    if (path.includes(ROUTES.admin.users.path)) {
      return { pageName: 'Users', section: 'Admin' };
    }
    if (path.includes(ROUTES.admin.roles.path)) {
      return { pageName: 'Roles', section: 'Admin' };
    }
  }
  return { pageName: 'Unknown', section: 'Unknown' };
};

export { useRouteTracking };
